import { readonly, ref } from 'vue'
import { execute } from '@/graphql/client'
import { CheckCatalogueDocument, type CheckCatalogueQuery } from '@/graphql/generated/graphql'

export type CatalogueEntry = CheckCatalogueQuery['checks'][number]

/**
 * The check catalogue, fetched once and shared.
 *
 * The schema says the set is fixed at server start and "worth caching for the
 * life of a page", so this is a module-level cache rather than a store. It is
 * derived data with one writer and no user-visible state, which is not what
 * Pinia is for.
 *
 * The report page uses it to put a sentence explaining what a check catches
 * next to the finding it produced. That description is deliberately not asked
 * for on `Finding.check`: it costs the server nothing there, but it repeats
 * across every finding a check produced, and at a few hundred findings polled
 * once a second it is the same ninety sentences over and over. Fetching the
 * catalogue once and joining on `checkId` keeps the polled response small.
 *
 * The join is supplementary. A failed catalogue fetch costs a sentence of
 * context and nothing else, so it does not surface an error.
 */

const entries = ref<readonly CatalogueEntry[]>([])
const byId = ref<ReadonlyMap<string, CatalogueEntry>>(new Map())
const loaded = ref(false)
const failed = ref(false)
let inFlight: Promise<void> | null = null

function load() {
  failed.value = false
  inFlight = execute(CheckCatalogueDocument)
    .then((data) => {
      entries.value = data.checks
      byId.value = new Map(data.checks.map((check) => [check.id, check]))
      loaded.value = true
    })
    .catch(() => {
      // On the report page this costs a sentence of context and nothing else,
      // so it stays quiet there. The catalogue page has nothing without it, and
      // reads `failed` to say so and offer a retry.
      failed.value = true
    })
    .finally(() => {
      inFlight = null
    })
}

export function useCatalogue() {
  if (!loaded.value && inFlight === null && !failed.value) load()

  return {
    entries: readonly(entries),
    loaded: readonly(loaded),
    failed: readonly(failed),
    reload: () => {
      if (inFlight === null) load()
    },
    describe: (checkId: string): string | undefined => byId.value.get(checkId)?.description,
  }
}

/** Test seam. Resets the module cache between specs. */
export function __resetCatalogue() {
  entries.value = []
  byId.value = new Map()
  loaded.value = false
  failed.value = false
  inFlight = null
}
